import isNil from "lodash-es/isNil";
import { makeAutoObservable } from "mobx";
import type { WretchError } from "wretch";

import { apiClient } from "../../common/apiClient";
import { logger } from "../../common/logger";
import { getBaseUrl } from "../../common/toggleBaseURL";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type { DeliveryWindowData } from "../../common/types/deliveryTypes";
import { translateWeekday } from "../../common/utils";
import {
   type AsyncData,
   initializeWithDefaultData,
   setAsDataAvailable,
   setAsErrorOccured,
   setAsLoading
} from "../../common/utils/asyncDataUtils";
import { englishDayToDay } from "../../common/utils/dateUtils";
import authStore from "../auth/authStore";

type DeliveryWindow = {
   time: string;
   weekday: string;
};

type DeliveryWindowResponse = {
   customerNumber: string;
   deliveries: Array<{
      day: string;
      deliveryWindows: Array<{
         start: string;
         stop: string;
      }>;
   }>;
};

type DeliveryWindowStoreType = BaseStoreType & {
   windows: AsyncData<DeliveryWindow[]>;
   windowsTableData: AsyncData<DeliveryWindowData[]>;
   fetchWindows: () => Promise<void>;
};

const deliveryWindowStore: DeliveryWindowStoreType = makeAutoObservable({
   windows: initializeWithDefaultData([]),
   windowsTableData: initializeWithDefaultData([]),

   fetchWindows: async () => {
      if (!authStore.isLoggedIn() || isNil(authStore.currentCompany)) {
         logger.warn("delivery", "Fetch delivery windows called without being logged in or without a current company");
         return;
      }

      const transitionedSuccessfully = setAsLoading(deliveryWindowStore.windows);
      if (!transitionedSuccessfully) {
         logger.log("delivery", "Already fetching delivery windows... aborting!");
         return;
      }
      setAsLoading(deliveryWindowStore.windowsTableData);

      const url = `${getBaseUrl("deliveryWindows")}/api/deliverywindow/${authStore.currentCompany}/delivery-windows`;
      //const resp: DeliveryWindowResponse = await;
      apiClient(url, authStore.getSessionToken())
         .get()

         .notFound(() => {
            logger.log("delivery", "No delivery windows found for customer");
            setAsDataAvailable(deliveryWindowStore.windows, []);
            setAsDataAvailable(deliveryWindowStore.windowsTableData, []);
         })

         .json((resp: DeliveryWindowResponse) => {
            const storeData = resp.deliveries.map((delivery) => ({
               time: delivery.deliveryWindows.map((window) => `${window.start} - ${window.stop}`).join(", "),
               weekday: translateWeekday(delivery.day)
            }));

            const windowsData = resp.deliveries
               .map((delivery) => ({
                  day: englishDayToDay(delivery.day),
                  periods: delivery.deliveryWindows
               }))
               .filter((windowsData) => typeof windowsData.day === "number");

            setAsDataAvailable(deliveryWindowStore.windows, storeData);
            setAsDataAvailable(deliveryWindowStore.windowsTableData, windowsData);
         })

         .catch((err: WretchError) => {
            logger.warn("delivery", "Failed to fetch delivery windows", err);
            setAsErrorOccured(deliveryWindowStore.windows, `${err}`);
            setAsErrorOccured(deliveryWindowStore.windowsTableData, `${err}`);
         });
   },
   clearCompanySpecificData: () => {
      deliveryWindowStore.windows = initializeWithDefaultData([]);
      deliveryWindowStore.windowsTableData = initializeWithDefaultData([]);
   }
} satisfies DeliveryWindowStoreType);

export default deliveryWindowStore;
